// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Framework product page (store front end). The framework itself is
// licensed separately under the Squarebit Framework End User License
// Agreement: https://store.squarebitstudios.com/sbframework/eula
// ==========================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotFound } from '../common/components/404.component';
import { useHasFrameworkAccess } from './feature/use-sbframework-access';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import packageJson from '../../../package.json';

const version = (packageJson as { sbframework?: { version?: string } }).sbframework
  ?.version;

/**
 * Invite-only product page for the Squarebit Framework.
 *
 * Gated the same way Riser's is - an account without the grant gets the
 * ordinary not-found page rather than a 403, so the route never confirms there
 * is anything behind it.
 *
 * One deliberate difference from Riser: this page's copy lives in the i18n
 * bundle rather than behind the API. Riser is unannounced, so its words could
 * not ship to every visitor; the framework has been sold to studios for years
 * and its existence is not the secret. What the grant protects here is the
 * download and the licence key, and both of those are checked server-side.
 */
const SbFrameworkPage: React.FC = () => {
  const { t } = useTranslation();
  const { granted, isLoading } = useHasFrameworkAccess();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[80px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Rendered in place rather than redirected to. A redirect rewrites the
  // URL, so a holder who lands here while the session is still resolving
  // ends up parked on /not-found with no way back to the page they asked
  // for. Rendering NotFound here leaves the address alone, so the real
  // page appears the moment access is known.
  if (!granted) return <NotFound />;

  const modules = [
    {
      title: t('sbframework.modules.coreTitle'),
      body: t('sbframework.modules.coreBody')
    },
    {
      title: t('sbframework.modules.mixTitle'),
      body: t('sbframework.modules.mixBody')
    },
    {
      title: t('sbframework.modules.sbguiTitle'),
      body: t('sbframework.modules.sbguiBody')
    },
    {
      title: t('sbframework.modules.templatesTitle'),
      body: t('sbframework.modules.templatesBody')
    }
  ];

  return (
    <div className="w-full max-w-[980px] mx-auto px-[20px] py-[56px]">
      {/* Hero */}
      <div className="flex items-center gap-[10px] mb-[14px]">
        <span className="inline-flex items-center px-[9px] py-[3px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide uppercase">
          {t('sbframework.hero.eyebrow')}
        </span>
        {version && (
          <Link
            to={ROUTER_LINKS.SBFRAMEWORK_CHANGELOG}
            className="inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide hover:bg-surface-hover-section transition-colors"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />
            {t('management.product.version', { version })}
          </Link>
        )}
      </div>

      <h1 className="text-[38px] leading-[1.1] font-display font-bold text-content-dark tracking-tight mb-[14px]">
        {t('sbframework.hero.title')}
      </h1>
      <p className="font-body text-[17px] leading-[1.6] text-content-secondary max-w-[680px] mb-[26px]">
        {t('sbframework.hero.subtitle')}
      </p>

      <div className="flex flex-col sm:flex-row gap-[12px] mb-[52px]">
        <Link
          to={ROUTER_LINKS.MANAGEMENT}
          className="inline-flex items-center justify-center py-[12px] px-[26px] rounded-[9px] text-[14px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base shadow-[0_2px_10px_rgba(14,165,201,0.3)] hover:bg-surface-popular hover:border-surface-popular transition-all duration-200 no-underline"
        >
          {t('sbframework.hero.download')}
        </Link>
        <Link
          to={ROUTER_LINKS.SBFRAMEWORK_DOCS}
          className="inline-flex items-center justify-center py-[12px] px-[26px] rounded-[9px] text-[14px] font-semibold border-[1.5px] border-border-dark/[0.18] text-content-dark hover:bg-surface-hover transition-all duration-200 no-underline"
        >
          {t('sbframework.hero.docs')}
        </Link>
      </div>

      {/* What's in it */}
      <h2 className="text-[22px] font-display font-bold text-content-dark tracking-tight mb-[18px]">
        {t('sbframework.modules.title')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] mb-[52px]">
        {modules.map((mod) => (
          <div
            key={mod.title}
            className="rounded-[14px] border border-border-dark/[0.11] bg-surface-base p-[20px]"
          >
            <h3 className="font-mono text-[13px] font-semibold text-blue mb-[8px]">
              {mod.title}
            </h3>
            <p className="font-body text-[14px] leading-[1.6] text-content-secondary">
              {mod.body}
            </p>
          </div>
        ))}
      </div>

      {/* Install + licensing */}
      <h2 className="text-[22px] font-display font-bold text-content-dark tracking-tight mb-[18px]">
        {t('sbframework.install.title')}
      </h2>
      <p className="font-body text-[15px] leading-[1.7] text-content-secondary mb-[22px] max-w-[720px]">
        {t('sbframework.install.body')}
      </p>
      <h3 className="text-[16px] font-display font-bold text-content-dark mb-[10px]">
        {t('sbframework.install.licenseTitle')}
      </h3>
      <p className="font-body text-[15px] leading-[1.7] text-content-secondary mb-[10px] max-w-[720px]">
        {t('sbframework.install.licenseBody')}
      </p>
      <p className="font-body italic text-[13px] text-content-muted-alt mb-[52px]">
        {t('sbframework.install.seatsNote')}
      </p>

      {/* Compatibility */}
      <h2 className="text-[22px] font-display font-bold text-content-dark tracking-tight mb-[18px]">
        {t('sbframework.compat.title')}
      </h2>
      <ul className="mb-[52px] flex flex-col gap-[8px]">
        {[
          t('sbframework.compat.maya'),
          t('sbframework.compat.platforms'),
          t('sbframework.compat.python')
        ].map((line) => (
          <li
            key={line}
            className="font-body text-[15px] text-content-secondary flex items-center gap-[10px]"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue shrink-0" />
            {line}
          </li>
        ))}
      </ul>

      {/* Support */}
      <div className="rounded-[16px] border border-border-dark/[0.11] bg-surface-hover p-[26px]">
        <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
          {t('sbframework.support.title')}
        </h2>
        <p className="font-body text-[15px] leading-[1.6] text-content-secondary mb-[16px]">
          {t('sbframework.support.body')}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center justify-center py-[10px] px-[20px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base hover:bg-surface-popular hover:border-surface-popular transition-all duration-200 no-underline"
        >
          {t('sbframework.support.cta')}
        </Link>
      </div>
    </div>
  );
};

export default SbFrameworkPage;
